/**
 * Image Hotspots with Accordion Integration
 *
 */

/**
 * Simple accordion utilities
 */
const AccordionUtils = {
  /**
   * Open a specific accordion by ID and scroll to it
   */
  async openAccordion(accordionId) {
    // Find accordion element by ID
    const accordion = this.findAccordionElement(accordionId);
    if (!accordion) {
      return false;
    }

    try {
      // Open the accordion
      if (accordion.tagName === "DETAILS" && !accordion.open) {
        accordion.open = true;
      }

      // Wait for content to expand
      await new Promise((resolve) => setTimeout(resolve, 150));

      // Scroll to accordion with offset
      const elementRect = accordion.getBoundingClientRect();
      const offset = 80;

      window.scrollTo({
        top: window.pageYOffset + elementRect.top - offset,
        behavior: "smooth",
      });

      // Add highlight effect
      accordion.classList.add("highlighted");
      setTimeout(() => {
        accordion.classList.remove("highlighted");
      }, 2000);

      return true;
    } catch (error) {
      return false;
    }
  },

  /**
   * Find accordion element by ID
   */
  findAccordionElement(accordionId) {
    // Extract the data-id from accordion-123 format
    const dataId = accordionId.replace("accordion-", "");

    const selectors = [
      `details.accordion[data-id="${dataId}"]`,
      `details[data-id="${dataId}"]`,
      `.accordion[data-id="${dataId}"]`,
      `.fl-accordion[data-id="${dataId}"]`,
      `[data-accordion-id="${dataId}"]`,
      `.smart-accordion[data-id="${dataId}"]`,
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        return element;
      }
    }

    return null;
  },

  /**
   * Get all available accordions for debugging
   */
  getAllAccordions() {
    const accordionSelectors = [
      "details.accordion[data-id]",
      "details[data-id]",
      ".accordion[data-id]",
      ".fl-accordion[data-id]",
      "[data-accordion-id]",
      ".smart-accordion[data-id]",
    ];

    const accordions = [];
    accordionSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element) => {
        const dataId =
          element.getAttribute("data-id") ||
          element.getAttribute("data-accordion-id");
        if (dataId) {
          // Try to find title
          const titleSelectors = [
            ".accordion__summary-title",
            ".accordion-title",
            "summary",
            ".title",
            "h3",
            "h4",
          ];
          let title = "Untitled";

          for (const titleSelector of titleSelectors) {
            const titleElement = element.querySelector(titleSelector);
            if (titleElement && titleElement.textContent.trim()) {
              title = titleElement.textContent.trim();
              break;
            }
          }

          accordions.push({
            id: `accordion-${dataId}`,
            label: title,
            dataId: dataId,
          });
        }
      });
    });

    return accordions;
  },
};

/**
 * Hotspot Controller
 */
class HotspotController {
  constructor() {
    this.initializeHotspots();
    this.setupAccordionLinks();
  }

  /**
   * Initialize all hotspot containers and their functionality
   */
  initializeHotspots() {
    const imageHotspots = document.querySelectorAll(".salsnes-image-hotspots");

    imageHotspots.forEach((hotspotContainer) => {
      const hotspots = hotspotContainer.querySelectorAll(
        ".salsnes-image-hotspots__hotspot"
      );
      const infoBoxes = hotspotContainer.querySelectorAll(
        ".salsnes-image-hotspots__info-box"
      );
      const modalOverlay = hotspotContainer.querySelector(
        ".salsnes-image-hotspots__modal-overlay"
      );
      const infoStyle = hotspotContainer.getAttribute("data-info-style");

      // Initialize hotspots
      hotspots.forEach((hotspot, index) => {
        const infoBox = hotspotContainer.querySelector(
          `.salsnes-image-hotspots__info-box[data-hotspot-index="${index}"]`
        );

        // Click handler
        hotspot.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleHotspot(hotspot, infoBox, infoStyle);
        });

        // Keyboard handler
        hotspot.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            this.toggleHotspot(hotspot, infoBox, infoStyle);
          }
        });

        // Hover effects for tooltip style
        if (infoStyle === "tooltip") {
          hotspot.addEventListener("mouseenter", () => {
            this.showInfoBox(infoBox);
          });

          hotspot.addEventListener("mouseleave", () => {
            this.hideInfoBox(infoBox);
          });

          hotspot.addEventListener("focus", () => {
            hotspot.classList.add("focused");
            this.showInfoBox(infoBox);
          });

          hotspot.addEventListener("blur", () => {
            hotspot.classList.remove("focused");
            this.hideInfoBox(infoBox);
          });
        }
      });

      // Close info boxes when clicking outside
      hotspotContainer.addEventListener("click", (e) => {
        if (
          e.target === hotspotContainer ||
          e.target.classList.contains("salsnes-image-hotspots__image")
        ) {
          this.closeAllInfoBoxes(infoBoxes);
        }
      });

      // Close modal when clicking overlay
      if (modalOverlay) {
        modalOverlay.addEventListener("click", () => {
          this.closeAllInfoBoxes(infoBoxes);
        });
      }

      // Close info boxes on escape key
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          this.closeAllInfoBoxes(infoBoxes);
        }
      });
    });
  }

  /**
   * Set up event listeners for accordion links
   */
  setupAccordionLinks() {
    const readMoreLinks = document.querySelectorAll(
      ".salsnes-image-hotspots__read-more-link"
    );

    readMoreLinks.forEach((link) => {
      const handleActivation = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const accordionTarget = link.getAttribute("data-accordion-target");
        const hotspotIndex = link.getAttribute("data-hotspot-index");

        if (!accordionTarget || !hotspotIndex) {
          return;
        }

        // Close the current hotspot info box
        this.closeHotspotInfoBox(hotspotIndex);

        // Try to open the linked accordion
        AccordionUtils.openAccordion(accordionTarget);
      };

      // Add click and keyboard handlers
      link.addEventListener("click", handleActivation);
      link.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleActivation(e);
        }
      });
    });
  }

  /**
   * Close a specific hotspot info box by index
   */
  closeHotspotInfoBox(hotspotIndex) {
    const infoBox = document.querySelector(
      `.salsnes-image-hotspots__info-box[data-hotspot-index="${hotspotIndex}"]`
    );
    const hotspot = document.querySelector(
      `.salsnes-image-hotspots__hotspot[data-hotspot-index="${hotspotIndex}"]`
    );

    if (infoBox && hotspot) {
      hotspot.classList.remove("active");
      this.hideInfoBox(infoBox);
    }
  }

  /**
   * Toggle hotspot visibility and state
   */
  toggleHotspot(hotspot, infoBox, infoStyle) {
    const isActive = hotspot.classList.contains("active");

    // Close all other hotspots first
    const allInfoBoxes = document.querySelectorAll(
      ".salsnes-image-hotspots__info-box"
    );
    allInfoBoxes.forEach((box) => {
      if (box !== infoBox) {
        const boxIndex = box.getAttribute("data-hotspot-index");
        const otherHotspot = document.querySelector(
          `.salsnes-image-hotspots__hotspot[data-hotspot-index="${boxIndex}"]`
        );
        if (otherHotspot) {
          otherHotspot.classList.remove("active");
        }
        this.hideInfoBox(box);
      }
    });

    if (!isActive && infoBox) {
      this.showInfoBox(infoBox);
      hotspot.classList.add("active");
    } else if (isActive && (infoStyle === "modal" || infoStyle === "side")) {
      hotspot.classList.remove("active");
      this.hideInfoBox(infoBox);
    }
  }

  /**
   * Show an info box with animation
   */
  showInfoBox(infoBox) {
    if (!infoBox) return;

    const isMobile = window.innerWidth <= 768;

    infoBox.style.display = "block";
    infoBox.classList.add("active");

    if (isMobile) {
      infoBox.classList.add("mobile-view");
    }

    // Show modal overlay if it exists
    const modalOverlay = infoBox
      .closest(".salsnes-image-hotspots")
      .querySelector(".salsnes-image-hotspots__modal-overlay");
    if (modalOverlay) {
      modalOverlay.style.display = "block";
      modalOverlay.classList.add("visible");
    }

    // Add animation
    requestAnimationFrame(() => {
      infoBox.classList.add("visible");

      if (isMobile) {
        setTimeout(() => {
          infoBox.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }, 300);
      }
    });
  }

  /**
   * Hide an info box with animation
   */
  hideInfoBox(infoBox) {
    if (!infoBox) return;

    infoBox.classList.remove("visible");
    infoBox.classList.remove("mobile-view");

    // Hide modal overlay if it exists
    const modalOverlay = infoBox
      .closest(".salsnes-image-hotspots")
      .querySelector(".salsnes-image-hotspots__modal-overlay");
    if (modalOverlay) {
      modalOverlay.classList.remove("visible");
      setTimeout(() => {
        modalOverlay.style.display = "none";
      }, 300);
    }

    setTimeout(() => {
      infoBox.style.display = "none";
      infoBox.classList.remove("active");
    }, 300);
  }

  /**
   * Close all info boxes
   */
  closeAllInfoBoxes(infoBoxes) {
    infoBoxes.forEach((infoBox) => {
      const hotspot = infoBox.closest(".salsnes-image-hotspots__hotspot");
      if (hotspot) {
        hotspot.classList.remove("active");
      }
      if (infoBox.style.display !== "none") {
        this.hideInfoBox(infoBox);
      }
    });
  }
}

/**
 * Initialize the image hotspots functionality
 */
function initializeImageHotspots() {
  try {
    const hotspotController = new HotspotController();

    // Store reference globally for debugging if needed
    window.SalsnesImageHotspots = {
      controller: hotspotController,
      openAccordion: (accordionId) => AccordionUtils.openAccordion(accordionId),
      getAccordions: () => AccordionUtils.getAllAccordions(),
      showAccordionHelp: () => {
        const accordions = AccordionUtils.getAllAccordions();

        if (accordions.length === 0) {
          console.log("No accordions found on the page");
          return [];
        }

        console.log("Available accordion IDs:");
        accordions.forEach((acc, index) => {
          console.log(`  ${index + 1}. "${acc.label}" → ID: ${acc.id}`);
        });

        console.log(
          'Copy the ID and paste it into the "Accordion ID" field in hotspot settings'
        );
        return accordions;
      },
    };

    return true;
  } catch (error) {
    console.error("Failed to initialize Image Hotspots:", error);
    return false;
  }
}

// Responsive resize handler
const handleResize = (() => {
  let timeout;
  return () => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      if (
        window.SalsnesImageHotspots &&
        window.SalsnesImageHotspots.controller
      ) {
        const infoBoxes = document.querySelectorAll(
          ".salsnes-image-hotspots__info-box.visible"
        );
        const isMobile = window.innerWidth <= 768;

        infoBoxes.forEach((infoBox) => {
          if (isMobile && !infoBox.classList.contains("mobile-view")) {
            infoBox.classList.add("mobile-view");
          } else if (!isMobile && infoBox.classList.contains("mobile-view")) {
            infoBox.classList.remove("mobile-view");
          }
        });
      }
    }, 250);
  };
})();

// Add resize listener
window.addEventListener("resize", handleResize);

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeImageHotspots);
} else {
  initializeImageHotspots();
}
